import { ChipProps } from '@c2fo/liquidity';
import { ClearingInvoice, ClearingInvoiceSearchFilters } from '@/enterprise/data/useClearingInvoices';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import { AwardedClearingStatus, AwardedInvoiceRow } from './types';

export const statusChipConfig: Record<
  AwardedClearingStatus,
  { labelKey: string; type: ChipProps['type']; tooltipKey: string }
> = {
  // Reason: match legacy getInvoiceDisplay — actively clearing invoices are labeled Cleared -> Awarded.
  clearing: {
    labelKey: 'core.awarded',
    type: 'default',
    tooltipKey: 'invoiceTables.awarded.pendingClearStatusTooltip',
  },
  pending_clear: {
    labelKey: 'core.pending',
    type: 'default',
    tooltipKey: 'invoiceTables.awarded.pendingClearStatusTooltip',
  },
  cleared: {
    labelKey: 'core.awarded',
    type: 'default',
    tooltipKey: 'invoiceTables.awarded.clearedStatusTooltip',
  },
};

/**
 * Join SEA clearing invoices with taker-market fields (IC eligible pattern).
 * Market clear date prefers invoice clear/scheduled dates, then marketNextClearTime.
 */
export const mapClearingInvoicesToAwardedRows = ({
  invoices,
  takerMarkets,
}: {
  invoices: ClearingInvoice[];
  takerMarkets: TakerMarket[];
}): AwardedInvoiceRow[] =>
  invoices.map((invoice) => {
    const matchingTakerMarket = takerMarkets.find(
      (tm) => tm.marketUuid === invoice.marketUuid && tm.takerDivisionUuid === invoice.supplierDivisionUuid
    );

    // Prefer invoice fields; fall back to market next clear (Node: clearDate ?? marketNextClearTime)
    const candidateClearDate =
      invoice.clearDate ?? invoice.scheduledClearDate ?? matchingTakerMarket?.marketNextClearTime;
    // Reason: treat empty string market dates as missing so the cell stays blank.
    const marketClearDate = candidateClearDate || null;

    return {
      voucherId: invoice.voucherId,
      amount: invoice.amount,
      currency: invoice.currency,
      dueDate: invoice.dueDate,
      newPayDate: invoice.newPayDate,
      minDiscount: invoice.minDiscount,
      earn: invoice.earn,
      clearingStatus: invoice.clearingStatus,
      marketClearDate,
    };
  });

/**
 * Awarded tab does not filter by clearingCategory (returns all clearing invoices for the market).
 * For manual-award markets, restricts to actively clearing status (Node parity).
 */
export const buildAwardedSearch = (takerMarkets: TakerMarket[]): ClearingInvoiceSearchFilters | undefined => {
  const isManuallyAwarded = takerMarkets.some((tm) => tm.offerConfig.isManuallyAwarded);

  if (!isManuallyAwarded) {
    return undefined;
  }

  return {
    clearingStatus: ['clearing'],
  };
};
